import { FC, useContext, useRef, useEffect } from 'react'
import { useRouter } from 'next/router'

import statisticContext from 'context/statisticsContext'

import Auth from 'store/auth'
import { postAxiosSingle } from 'pages/api/axios'
import { TICK } from '../data'
import { extractIdAndKey, checkJsonString } from 'utils/rutube/rutubeVideoUtils'
import { createQueryUnAuth } from './utils'

import iframeStyle from '../iframe.module.scss'

interface IRutubeVideo {
  videoLink: string
  materialId?: number | string
  materialType?: string
  noAnalytics?: boolean
  refPlayerOuter?: any
}

const RutubeVideo: FC<IRutubeVideo> = ({
  videoLink,
  materialId,
  materialType,
  noAnalytics,
  refPlayerOuter,
}) => {
  const socket = useContext(statisticContext)
  const interval = useRef<ReturnType<typeof setInterval> | null>(null)
  const { rutubeVideoId, rutubePrivatkey } = extractIdAndKey(videoLink)
  const { fakeAuth } = Auth.useAuth()
  const router = useRouter()

  const refPlayer = useRef<any>()
  const counterStartRu = useRef(0)
  const removeInterval = () => {
    if (interval.current === null) return

    clearInterval(interval.current)
    interval.current = null
  }

  const sendData = (eventName: string) => {
    if (Object.keys(socket).length) {
      socket?.sendEvent(eventName, {
        id: materialId,
        materialType: materialType,
        timestamp: Date.now(),
      })
    }
  }

  const handleStartVideo = () => {
    if (materialId && materialType) {
      sendData('playVideoRecord')
      removeInterval()

      if (counterStartRu.current === 0 && router.query.spec && fakeAuth)
        postAxiosSingle(createQueryUnAuth(router.query.spec), {}, { materialId, materialType })

      counterStartRu.current += 1
      interval.current = setInterval(() => sendData('playVideoRecord'), TICK)
    }
  }

  const handlePauseVideo = () => {
    sendData('pauseVideoRecord')
    removeInterval()
    interval.current = null
  }

  const getStatistic = (event: MessageEvent) => {
    if (checkJsonString(event.data)) {
      const message = JSON.parse(event.data)

      if (message.type === 'player:changeState') {
        switch (message.data.state) {
          case 'playing':
            handleStartVideo()
            break
          case 'paused':
            handlePauseVideo()
            break
          case 'stopped':
            handlePauseVideo()
            break
        }
      }
    }
  }

  useEffect(() => {
    if (refPlayerOuter) refPlayerOuter.current = refPlayer.current
    if (noAnalytics) return
    window.addEventListener('message', getStatistic)

    return () => {
      if (interval?.current) {
        sendData('pauseVideoRecord')
        clearInterval(interval.current)
        window.removeEventListener('message', getStatistic)
      }
    }
  }, [refPlayer, socket])

  return (
    <iframe
      width="100%"
      height="100%"
      src={`https://rutube.ru/play/embed/${rutubeVideoId}/?p=${rutubePrivatkey}`}
      className={iframeStyle.playerIframe}
      ref={refPlayer}
      frameBorder="0"
      allow="clipboard-write; autoplay; fullscreen"
      allowFullScreen
    ></iframe>
  )
}

export default RutubeVideo
